import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { type ReactElement, type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';

import type { EmergencyStopChannelResult, SubscriptionId } from '../../api';
import { Amount } from '../../components/Amount';
import { StatusChip } from '../../components/StatusChip';
import { reasonMessage } from '../../i18n';
import type { ApiError } from '../../lib/api/api-client';
import { homeLinks } from '../home/links';

import { PositionLabel } from './PositionLabel';
import {
  allChannelsPaused,
  type ChannelRow,
  channelRowFrom,
  closingReason,
  hasClosesInProgress,
  hasFailedCloses,
  type PositionRow,
  type StopSummary,
} from './emergency-model';
import { ROW_SEPARATOR_SX } from './row-style';

/** Props of {@link EmergencyStopResult}. */
export interface EmergencyStopResultProps {
  /** Every stop's answer of this visit, merged (the latest answer wins per channel and position). */
  readonly summary: StopSummary;
  /** Failure of a channel's last retry, shown on its row instead of the stop's reason (I12). */
  readonly channelErrors: ReadonlyMap<SubscriptionId, ApiError>;
  /**
   * A write is in flight (a channel's retry, or "Retry closing" in its sheet): the other retry
   * buttons wait and the summary cannot be left, as the sheet cannot (C3 R11).
   */
  readonly writing: boolean;
  /** Retry tapped on a failed channel's row (M4 §4 "Partial pause"). */
  readonly onRetryChannel: (subscriptionId: SubscriptionId) => void;
  /**
   * "Retry closing" tapped: the confirmation opens again with the current positions, which also
   * mark the rows closed meanwhile as "No longer open" (Q104).
   */
  readonly onRetryClose: () => void;
}

// dir="auto": a channel named in Arabic or Hebrew keeps its own direction (C3 R7). A constant, not a text.
const OWN_DIRECTION = 'auto';

/**
 * E10's summary after the stop (M4 R10, R11, R16): one row per channel (paused, or failed with
 * `EMERGENCY_PAUSE_FAILED` and a retry) and one per position with its close status. One failure
 * never hides the others. "All copying is stopped" shows only once every channel is paused, and
 * never while a stop that asked to close could not say what it closed (Q107).
 */
export function EmergencyStopResult({
  summary,
  channelErrors,
  writing,
  onRetryChannel,
  onRetryClose,
}: EmergencyStopResultProps): ReactElement {
  const { t } = useTranslation();
  const channelsTitle = useId();
  const positionsTitle = useId();
  const notClosed = summary.positionsNotClosed;
  const rows = summary.channels.map((channel) => ({
    channel,
    row: summary.rows.get(channel.subscriptionId) ?? channelRowFrom(channel),
  }));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 1 }}>
      <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
        {t('emergency.result.title')}
      </Typography>
      {notClosed ? (
        // Q107 (provisional): the API answered PARTIAL with no row that explains it, e.g. it could
        // not read the positions. Never "All copying is stopped": nothing says they were closed.
        <Alert severity="error">{t('emergency.result.positionsNotClosed')}</Alert>
      ) : (
        allChannelsPaused(rows.map(({ row }) => row)) && (
          <Alert severity="success">{t('emergency.result.allStopped')}</Alert>
        )
      )}
      {rows.length > 0 && (
        <Section titleId={channelsTitle} title={t('emergency.result.channels')}>
          {rows.map(({ channel, row }) => (
            <ChannelLine
              key={channel.subscriptionId}
              channel={channel}
              row={row}
              error={channelErrors.get(channel.subscriptionId) ?? null}
              writing={writing}
              onRetry={() => {
                onRetryChannel(channel.subscriptionId);
              }}
            />
          ))}
        </Section>
      )}
      {summary.positions.length > 0 && (
        <Section titleId={positionsTitle} title={t('emergency.result.positions')}>
          {summary.positions.map((position) => (
            <PositionLine key={position.positionId} position={position} />
          ))}
        </Section>
      )}
      {(notClosed || hasFailedCloses(summary.positions)) && (
        // Q104 (provisional): the sheet opens again with the current positions; it confirms only
        // with the option checked, and sends a new stop with a new key, kept across a retry of
        // that same attempt (M4 R13, S2 R13). Rows closed meanwhile read "No longer open".
        <Button
          variant="outlined"
          color="error"
          size="large"
          disabled={writing}
          onClick={onRetryClose}
        >
          {t('emergency.result.retryClose')}
        </Button>
      )}
      {(notClosed || hasClosesInProgress(summary.positions)) && (
        <Button
          component={RouterLink}
          to={homeLinks.positions}
          variant="outlined"
          size="large"
          disabled={writing}
        >
          {t('emergency.result.followOnPositions')}
        </Button>
      )}
      <Button component={RouterLink} to="/" variant="contained" size="large" disabled={writing}>
        {t('emergency.result.backHome')}
      </Button>
    </Box>
  );
}

/** A titled list in a card; the title names the list for assistive technologies. */
function Section({
  titleId,
  title,
  children,
}: {
  titleId: string;
  title: string;
  children: ReactNode;
}): ReactElement {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 3.5, px: 2, py: 1.5 }}>
      <Typography id={titleId} component="h2" sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>
        {title}
      </Typography>
      <Box component="ul" aria-labelledby={titleId} sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {children}
      </Box>
    </Paper>
  );
}

const LINE_SX = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  py: 1,
  ...ROW_SEPARATOR_SX,
} as const;

function ChannelLine({
  channel,
  row,
  error,
  writing,
  onRetry,
}: {
  channel: EmergencyStopChannelResult;
  row: ChannelRow;
  error: ApiError | null;
  writing: boolean;
  onRetry: () => void;
}): ReactElement {
  const { t } = useTranslation();
  // The row retry's own failure wins; otherwise the stop's reason (EMERGENCY_PAUSE_FAILED).
  const reason =
    row.kind === 'failed'
      ? error === null
        ? reasonMessage(row.reasonCode ?? undefined)
        : reasonMessage(error.reasonCode, error.params)
      : null;
  return (
    <Box component="li" sx={LINE_SX}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box
          component="span"
          dir={OWN_DIRECTION}
          sx={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}
        >
          {channel.channelTitle}
        </Box>
        {row.kind === 'paused' && (
          <StatusChip tone="neutral">{t('emergency.result.paused')}</StatusChip>
        )}
      </Box>
      {row.kind === 'failed' && reason !== null && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
        >
          <Typography sx={{ fontSize: 14, color: 'error.main' }}>
            {t(reason.key, reason.values)}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="error"
            loading={row.retrying}
            disabled={writing}
            onClick={onRetry}
            sx={{ flex: 'none', minHeight: 36 }}
          >
            {t('emergency.result.retry')}
          </Button>
        </Box>
      )}
    </Box>
  );
}

function PositionLine({ position }: { position: PositionRow }): ReactElement {
  const { t } = useTranslation();
  const reasonCode = closingReason(position);
  const reason = reasonCode === null ? null : reasonMessage(reasonCode);
  return (
    <Box component="li" sx={LINE_SX}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, fontSize: 14 }}>
        <PositionLabel side={position.side} symbol={position.symbol} lots={position.lots} />
        {position.result !== null && <Amount {...position.result} signed />}
      </Box>
      <Typography
        sx={{
          fontSize: 13,
          color: position.status === 'CLOSE_FAILED' ? 'error.main' : 'text.secondary',
        }}
      >
        {t(`emergency.result.status.${position.status}`)}
      </Typography>
      {reason !== null && (
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {t(reason.key, reason.values)}
        </Typography>
      )}
    </Box>
  );
}
